import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSiteSettings } from "@/data/useSiteSettings";
import { FINAL_CTA_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

export default function FinalCTA({ data }) {
  const settings = useSiteSettings();
  const merged = mergeSection(FINAL_CTA_DEFAULT, settings?.homepage?.final_cta);
  const content = mergeSection(merged, data);

  return (
    <section data-testid="final-cta-section" className="bg-slate-900 py-16 lg:py-24 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-[20%] w-80 h-80 bg-sky-500/6 rounded-full blur-[120px]" />
        <div className="absolute bottom-10 right-[15%] w-96 h-96 bg-sky-400/4 rounded-full blur-[140px]" />
      </div>

      {/* Top wave transition */}
      <div className="absolute top-0 left-0 right-0">
        <svg viewBox="0 0 1440 48" fill="none" preserveAspectRatio="none" className="w-full h-8 lg:h-12 rotate-180">
          <path d="M0 48V24C360 0 720 0 1080 24C1260 36 1380 48 1440 48H0Z" fill="white" />
        </svg>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative"
      >
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
          {content.heading}
        </h2>
        <p className="mt-5 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl mx-auto">
          {content.intro}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={content.primary_cta_href}>
            <Button
              data-testid="cta-request-demo-btn"
              className="btn-premium bg-sky-500 hover:bg-sky-400 text-white rounded-full px-8 h-12 text-sm font-semibold shadow-lg shadow-sky-500/25"
            >
              {content.primary_cta_label}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to={content.secondary_cta_href}>
            <Button
              variant="outline"
              data-testid="cta-why-antera-btn"
              className="btn-premium rounded-full px-8 h-12 text-sm font-semibold border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              {content.secondary_cta_label}
            </Button>
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
